import UpcomingOutlined from "@mui/icons-material/UpcomingOutlined";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { DataGridPro, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type FC, useMemo } from "react";
import {
  filterStatType,
  type GroupedMomentAnalyticsData,
} from "../../../../modules/stats/server/analytics-service";
import type { ISO8601 } from "../../../../utils/iso8601";
import { AnalyticsDatetime } from "../../utils/analytics-datetime";
import { EmptyState } from "../generic/empty-state";
import { ExportToolbar } from "../generic/export-toolbar";
import { calculatePercentage } from "../utils";

export const AnalyticsSendsTableExpandedDeliveries: FC<{
  stats: GroupedMomentAnalyticsData["stats"];
  switchToIssuesTab: () => void;
}> = ({ stats, switchToIssuesTab }) => {
  const { data, overall, errorCount } = useMemo(() => {
    const deliveries = filterStatType("delivery", stats);
    const errors = filterStatType("error", stats);

    return {
      data: deliveries.map((item) => item.data),
      errorCount: errors.length,
      overall: { total: deliveries.length + errors.length, count: deliveries.length },
    };
  }, [stats]);

  const rows = useMemo(() => {
    return data.map((item, id) => {
      return {
        id,
        first_name: item.recipient_data?.first_name ?? "-",
        last_name: item.recipient_data?.last_name ?? "-",
        work_email: item.recipient_data?.email ?? "-",
        delivery_address: item.recipient_data?.display ?? "-",
        delivery_time: item.timestamp,
      };
    });
  }, [data]);

  const columns = useMemo(() => {
    const columnData: GridColDef<(typeof rows)[0]>[] = [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "work_email",
        headerName: "Work Email",
        flex: 1,
      },
      {
        field: "delivery_address",
        headerName: "Delivery Address",
        flex: 1,
      },
      {
        field: "delivery_time",
        headerName: "Delivered",
        flex: 1.5,
        renderCell: (params: GridRenderCellParams<(typeof rows)[0], ISO8601>) =>
          params.value ? (
            <Stack direction="row" spacing={1} alignItems="center" height="100%">
              <AnalyticsDatetime datetime={params.value} />
            </Stack>
          ) : null,
      },
    ];
    return columnData;
  }, []);

  const percent = calculatePercentage(overall.count, overall.total);

  const isSendToChannel = useMemo(
    () => data.length === 1 && data[0]!.recipient_data?.is_multiperson,
    [data],
  );

  if (!rows.length)
    return (
      <EmptyState
        symbol={<UpcomingOutlined fontSize="large" />}
        title="No deliveries yet"
        subtitle="Check back soon"
      />
    );

  return (
    <Stack spacing={2} direction="column">
      <Stack direction="row" spacing={2} alignItems="flex-end">
        <Typography variant="h4">{`${percent}%`}</Typography>
        {!isSendToChannel && (
          <Typography variant="body1" color="text.secondary">
            ({overall.count} {overall.count === 1 ? "Recipient" : "Recipients"})
          </Typography>
        )}
      </Stack>
      {!isSendToChannel && (
        <DataGridPro
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          slots={{ toolbar: ExportToolbar }}
          showToolbar
        />
      )}
      {errorCount > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              data-analytics-id="switch-to-delivery-tab"
              onClick={switchToIssuesTab}
              variant="text"
              color="inherit"
            >
              View delivery issues
            </Button>
          }
        >
          {errorCount} ({calculatePercentage(errorCount, overall.total)}%){" "}
          {errorCount === 1 ? "recipient" : "recipients"} had delivery issues
        </Alert>
      )}
    </Stack>
  );
};
